import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import CompanyContext from '../../auth/CompanyContext';
import Loading from '../Loading/Loading';
import PortalDashboard from '../DashboardTiles/Dashboards/PortalDashboard';
import * as utilities from '../../utilities';
import ContactPercentageRing from './ContactPercentageRing';
import {
    getEngagementInsights,
    getFieldValues,
    getRelationshipSignals,
    getRelationshipTotals,
    toNumber
} from './contactInsights';

const formatCurrency = (value) => {
    const numberValue = Number(value) || 0;
    return numberValue.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
};

const renderBulletList = (items = []) => (
    <ul className="contactsCoreWidgetReasons contactsDashboardWidgetReasons small mb-0">
        {(items && items.length > 0 ? items : ['No Data Available']).map(item => (
            <li key={item}>{item}</li>
        ))}
    </ul>
);

const ContactsDashboard2 = ({
    isActive,
    crmEnabled = false,
    companySettings,
    selectedContactHouseholds = [],
    contactAccountsLoading = false,
    householdsLoading = false,
    contactGUID,
    clientToken,
    idToken,
    row,
    editCategoryCommentsArray = [],
    companies = [],
    ...portalDashboardProps
}) => {
    const companyCtx = useContext(CompanyContext);
    const hasCompanyPortalDatabase = String(companySettings?.CompanyPortalDatabase || '').trim() !== '';
    const mappedAccounts = useMemo(() => (selectedContactHouseholds || [])
        .map(item => String(item || '').trim())
        .filter(Boolean), [selectedContactHouseholds]);

    const [notes, setNotes] = useState([]);
    const [notesLoading, setNotesLoading] = useState(false);
    const [marketingData, setMarketingData] = useState({
        workflows: [],
        activities: [],
        opportunities: []
    });
    const [marketingLoading, setMarketingLoading] = useState(false);
    const [investmentLoading, setInvestmentLoading] = useState(false);
    const [investmentMarketValue, setInvestmentMarketValue] = useState(null);
    const [investmentAsOf, setInvestmentAsOf] = useState('');

    const fieldValues = useMemo(() => (
        getFieldValues(row, editCategoryCommentsArray)
    ), [editCategoryCommentsArray, row]);

    const loadNotes = useCallback(() => {
        if (!isActive || !contactGUID || !clientToken || !companyCtx.company) return;

        setNotesLoading(true);
        fetch(`/api/contactsNotes/${contactGUID}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + clientToken,
                'X-Database': companyCtx.company
            }
        })
            .then(results => results.json())
            .then(results => {
                setNotes(results?.data || []);
            })
            .catch(() => {
                setNotes([]);
                utilities.addToast('Unable to load contact notes.', 5, 'danger');
            })
            .finally(() => setNotesLoading(false));
    }, [isActive, contactGUID, clientToken, companyCtx.company]);

    const loadMarketingData = useCallback(() => {
        if (!isActive || !contactGUID || !clientToken || !companyCtx.company) return;

        setMarketingLoading(true);
        fetch(`/api/contacts/marketingActivities/${contactGUID}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + clientToken,
                'X-Database': companyCtx.company
            }
        })
            .then(results => results.json())
            .then(results => {
                setMarketingData({
                    workflows: results.data?.workflows || [],
                    activities: results.data?.activities || [],
                    opportunities: results.data?.opportunities || []
                });
            })
            .catch(() => {
                setMarketingData({
                    workflows: [],
                    activities: [],
                    opportunities: []
                });
                utilities.addToast('Unable to load contact relationship data.', 5, 'danger');
            })
            .finally(() => setMarketingLoading(false));
    }, [isActive, contactGUID, clientToken, companyCtx.company]);

    const getLatestHoldingsDate = useCallback(async (household) => {
        const results = await fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/posDates`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + (idToken || ''),
                'X-Database': companyCtx.company
            },
            body: JSON.stringify({
                port: '',
                household
            })
        });
        if (results.status === 401 || results.status === 403) throw new Error('Portal+ authorization failed');
        const response = await results.json();
        if (response.code === 200 && response.data && response.data.length > 0) return response.data[0].fDate;
        return '';
    }, [idToken, companyCtx.company]);

    const getHoldings = useCallback(async (household, dateValue) => {
        const dateObj = dateValue ? new Date(dateValue) : null;
        const params = new URLSearchParams({
            household,
            date: dateObj && !Number.isNaN(dateObj.getTime()) ? dateObj.toISOString() : ''
        });
        const results = await fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/holdingsSnapshot?${params.toString()}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + (idToken || ''),
                'X-Database': companyCtx.company
            }
        });
        if (results.status === 401 || results.status === 403) throw new Error('Portal+ authorization failed');
        const response = await results.json();
        if (response.code !== 200) return [];
        return response.data || [];
    }, [idToken, companyCtx.company]);

    const getHoldingMarketValue = useCallback((holding = {}) => (
        toNumber(
            holding.marketValue
            ?? holding.MarketValue
            ?? holding.market_value
            ?? holding.value
            ?? holding.Value
        )
    ), []);

    const loadInvestmentMarketValue = useCallback(async () => {
        if (!isActive) return;
        if (!crmEnabled || !hasCompanyPortalDatabase || !idToken || !companyCtx.company) {
            setInvestmentMarketValue(null);
            setInvestmentAsOf('');
            return;
        }
        if (contactAccountsLoading || householdsLoading) return;
        if (mappedAccounts.length === 0) {
            setInvestmentMarketValue(null);
            setInvestmentAsOf('');
            return;
        }

        setInvestmentLoading(true);
        try {
            let totalValue = 0;
            let latestDate = '';
            const values = await Promise.all(mappedAccounts.map(async household => {
                const date = await getLatestHoldingsDate(household);
                const holdings = await getHoldings(household, date);
                return {
                    date,
                    value: holdings.reduce((sum, holding) => sum + getHoldingMarketValue(holding), 0)
                };
            }));

            values.forEach(item => {
                totalValue += item.value;
                if (item.date && (!latestDate || new Date(item.date) > new Date(latestDate))) latestDate = item.date;
            });

            setInvestmentMarketValue(totalValue);
            setInvestmentAsOf(latestDate);
        } catch (err) {
            setInvestmentMarketValue(null);
            setInvestmentAsOf('');
            utilities.addToast('Unable to load investment market value from Portal+.', 5, 'danger');
        } finally {
            setInvestmentLoading(false);
        }
    }, [
        isActive,
        crmEnabled,
        hasCompanyPortalDatabase,
        idToken,
        companyCtx.company,
        contactAccountsLoading,
        householdsLoading,
        mappedAccounts,
        getLatestHoldingsDate,
        getHoldings,
        getHoldingMarketValue
    ]);

    useEffect(() => {
        loadNotes();
    }, [loadNotes]);

    useEffect(() => {
        loadMarketingData();
    }, [loadMarketingData]);

    useEffect(() => {
        loadInvestmentMarketValue();
    }, [loadInvestmentMarketValue]);

    const relationshipTotals = useMemo(() => (
        getRelationshipTotals(marketingData.opportunities || [])
    ), [marketingData.opportunities]);

    const engagement = useMemo(() => (
        getEngagementInsights({
            notes,
            marketingData,
            companies,
            fieldValues,
            relationshipTotals
        })
    ), [notes, marketingData, relationshipTotals, companies, fieldValues]);

    const relationshipActivitySignals = useMemo(() => (
        getRelationshipSignals({
            notes,
            marketingData
        })
    ), [notes, marketingData]);

    const relationshipSignals = useMemo(() => ([
        `Sales ${formatCurrency(relationshipTotals.sales)}${relationshipTotals.salesCount > 0 ? ` (${relationshipTotals.salesCount})` : ''}`,
        `Opportunities ${formatCurrency(relationshipTotals.opportunities)}${relationshipTotals.opportunityCount > 0 ? ` (${relationshipTotals.opportunityCount})` : ''}`,
        `${marketingData.workflows.length} Workflow${marketingData.workflows.length === 1 ? '' : 's'} Or Campaign${marketingData.workflows.length === 1 ? '' : 's'}`,
        `${marketingData.activities.length} Marketing Activit${marketingData.activities.length === 1 ? 'y' : 'ies'}`,
        relationshipTotals.excludedCount > 0
            ? `${relationshipTotals.excludedCount} Non Value Status Record${relationshipTotals.excludedCount === 1 ? '' : 's'} Excluded`
            : 'No Non Value Status Records'
    ]), [relationshipTotals, marketingData.workflows.length, marketingData.activities.length]);

    const insightsLoading = notesLoading || marketingLoading;
    const investmentWidgetLoading = investmentLoading || contactAccountsLoading || householdsLoading;
    const portalMappingLoading = contactAccountsLoading || householdsLoading;

    if (!isActive) return null;

    return (
        <div className="contactsDashboard">
            <Row className="dashboardTilesRow contactsDashboardInsightsRow text-left">
                <Col md={3} className="dashboardTile px-2 pb-3">
                    <Card className="w-100 m-0">
                        <CardHeader>
                            <h4>Relationship Value</h4>
                        </CardHeader>
                        <CardBody className="p-3">
                            {insightsLoading ? (
                                <Loading />
                            ) : (
                                <>
                                    <div className="contactsCoreRelationshipValue">
                                        {formatCurrency(relationshipTotals.total)}
                                    </div>
                                    {renderBulletList(relationshipSignals)}
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
                <Col md={3} className="dashboardTile px-2 pb-3">
                    <Card className="w-100 m-0">
                        <CardHeader>
                            <h4>Investment Market Value</h4>
                        </CardHeader>
                        <CardBody className="p-3">
                            {investmentWidgetLoading ? (
                                <Loading />
                            ) : (
                                <>
                                    <div className="contactsCoreRelationshipValue">
                                        {investmentMarketValue == null ? '—' : formatCurrency(investmentMarketValue)}
                                    </div>
                                    {renderBulletList([
                                        `${mappedAccounts.length} Mapped Account${mappedAccounts.length === 1 ? '' : 's'}`,
                                        investmentAsOf ? `As Of ${new Date(investmentAsOf).toLocaleDateString('en-US', { timeZone: 'UTC' })}` : 'No Holdings Date'
                                    ])}
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
                <Col md={3} className="dashboardTile px-2 pb-3">
                    <Card className="w-100 m-0">
                        <CardHeader>
                            <h4>Engagement Level</h4>
                        </CardHeader>
                        <CardBody className="p-3">
                            {insightsLoading ? (
                                <Loading />
                            ) : (
                                <Row className="align-items-center">
                                    <Col sm={4} className="contactsCoreFieldLabel">
                                        <ContactPercentageRing percentage={engagement.percentage} />
                                    </Col>
                                    <Col sm={8}>
                                        {renderBulletList(engagement.reasons)}
                                    </Col>
                                </Row>
                            )}
                        </CardBody>
                    </Card>
                </Col>
                <Col md={3} className="dashboardTile px-2 pb-3">
                    <Card className="w-100 m-0">
                        <CardHeader>
                            <h4>Relationship Signals</h4>
                        </CardHeader>
                        <CardBody className="p-3">
                            {insightsLoading ? <Loading /> : renderBulletList(relationshipActivitySignals)}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {crmEnabled && hasCompanyPortalDatabase && mappedAccounts.length > 0 ? (
                <PortalDashboard
                    {...portalDashboardProps}
                    clientToken={clientToken}
                    idToken={idToken}
                    contactGUID={contactGUID}
                    companySettings={companySettings}
                    contactAccountsLoading={contactAccountsLoading}
                    householdsLoading={householdsLoading}
                    selectedContactHouseholds={mappedAccounts}
                />
            ) : (crmEnabled && hasCompanyPortalDatabase && portalMappingLoading) ? (
                <Alert color="light">
                    Loading mapped households...
                    <Loading />
                </Alert>
            ) : (
                <Alert color="light">
                    {!crmEnabled || !hasCompanyPortalDatabase
                        ? 'Portal+ dashboard widgets are not available for this company.'
                        : 'No mapped Portal+ accounts are tied to this contact yet. Add account mappings to populate dashboard widgets.'}
                </Alert>
            )}
        </div>
    );
};

export default ContactsDashboard2;
